import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { QueryClient } from "@tanstack/react-query";
import { ChevronsRight } from "lucide-react";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import GoBackButton from "@/common/components/GoBackButton";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import useCreateHoliday from "../../hooks/useCreateHolidays";
import { FormTransformers } from "@/common/utils/formTransformers";
import { createSchema, HolidayValidationSchema } from "../../schemas/create.schema";
import { DateInput } from "@/common/components/DateInput/DateInput";
import Checkbox from "@/common/components/Checkbox";
import { FilterOptionOption } from "react-select";
import { filterOptions } from "@/features/users/constants/filterOptions.constant";
import { GeoTransformers } from "@/common/services/geoTransformers";
import useDebounce from "@/common/hooks/useDebounce";
import { useGeosOptions } from "@/common/hooks/queries/useGeosOptions";

const Create = () => {
  const navigate = useNavigate();
  const queryClient = new QueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [geoSearch, setGeoSearch] = useDebounce('')

  const geos = useGeosOptions(geoSearch)

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<HolidayValidationSchema>({
    resolver: zodResolver(createSchema)
  });

  const {
    mutate,
    isPending
  } = useCreateHoliday({
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['holidays']})
      navigate('/holidays')
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError, setMessages })(error)
      }
    }
  })

  const onSubmit: SubmitHandler<HolidayValidationSchema> = (data) => mutate(data);

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="false">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/holidays" searchLastRouteFromFallback />

          <p className="text-white-400 font-normal">
            Feriados
          </p>
          <ChevronsRight color='#ffffffb3' />

          <h2>
            Novo feriado
          </h2>
        </div>

        <div className="flex gap-4">
          <Link to='/holidays'>
            <Button variant='outline-error'>
              Cancelar
            </Button>
          </Link>

          <Button data-testid='create_holiday' type="submit" className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]" isLoading={isPending}>
            Confirmar
          </Button>
        </div>
      </div>

      <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
        <p className="text-lg font-bold">
          Novo feriado
        </p>

        <div className="flex gap-2">
          <TextField
            label="Nome do feriado"
            markAsRequired
            errorMessage={errors?.display_name?.message}
            {...register('display_name')}
            className="w-full"
          />
          <DateInput
            fieldName="date"
            control={control}
            errors={errors}
            label="Data"
            markAsRequired
          />
        </div>

        <TextArea
          label="Descrição"
          markAsRequired
          errorMessage={errors?.description?.message}
          {...register('description')}
          maxLength={512}
        />

        <Checkbox
          label="Bloquear adiantamento de fatura para essa data"
          errorMessage={errors?.blocks_establishment_payment_advances?.message || undefined}
          {...register('blocks_establishment_payment_advances')}
        />

        <SelectController
          control={control}
          name="geo"
          label="Aplicável na região"
          filterOption={
            filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean
          }
          defaultValue={GeoTransformers.mapToGeo()}
          options={GeoTransformers.mapToGeoOptions()(geos)}
          errorMessage={errors?.geo?.message || undefined}
          markAsRequired
          onInputChange={setGeoSearch}
        />

      </Box>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Create;
